/**
 * Workflow Presentation Editor Sidebar
 * 
 * Allows editing of workflow presentation (variable order and labels) with drag-and-drop.
 * Mimics the Studio sidebar rendering but with editing capabilities.
 */

import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { AssetPicker } from '../../components/AssetPicker';
import { assetService, Asset } from '../../services/assetService';


/**
 * Generate a URL-safe slug from a title string
 * "DreamShaper 8" → "dreamshaper_8"
 * "SDXL 1.0 (Turbo)" → "sdxl_1_0_turbo"
 */
const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, '_')        // Replace spaces with underscores
    .replace(/_+/g, '_')         // Collapse multiple underscores
    .replace(/^_|_$/g, '');      // Trim leading/trailing underscores
};

interface RichDropdownItem {
  value: string;
  title: string;
  subtitle?: string;
  thumbnail?: string; // Can be asset:// URI or full URL
}

interface WorkflowPresentationEditorProps {
  workflowId: string;
  tenantId: string;
  apiBaseUrl: string;
  authToken: string;
  onClose: () => void;
  onUpdate: () => void;
}

interface FieldElement {
  id: string; // Unique ID for drag-and-drop
  type: 'Control';
  scope: string; // e.g., "#/properties/prompt"
  label?: string; // Custom label (from options.label or schema.title)
  description?: string; // Field description (from schema.description)
  enumValues?: string[]; // For select widgets (from schema)
  default?: any; // Custom default value (from jsonSchema.properties[field].default)
  required?: boolean; // Whether the field is in jsonSchema.required[]
  options?: {
    widget?: string;
    label?: string;
    helpText?: string; // Help text shown in Studio as tooltip
    customEnumOptions?: string[]; // Custom override for enum values (per workflow)
    richDropdownItems?: RichDropdownItem[]; // Items for rich-dropdown widget
    hidden?: boolean; // Hide field from Studio tab while still passing default to execution
  };
}

interface GroupElement {
  id: string;
  type: 'Group';
  label: string;
  elements: FieldElement[];
  options?: {
    collapsible?: boolean;
  };
}

type UIElement = FieldElement | GroupElement;

export const WorkflowPresentationEditor: React.FC<WorkflowPresentationEditorProps> = ({
  workflowId,
  tenantId,
  apiBaseUrl,
  authToken,
  onClose,
  onUpdate
}) => {
  const [workflow, setWorkflow] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable UI elements
  const [uiElements, setUiElements] = useState<UIElement[]>([]);
  const [originalElements, setOriginalElements] = useState<string>('');

  // Asset picker state for rich-dropdown configuration
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [editingRichDropdownItem, setEditingRichDropdownItem] = useState<{
    fieldId: string;
    itemIndex: number;
    groupIndex?: number;
  } | null>(null);

  // Cache for resolved asset:// URLs
  const [resolvedAssetUrls, setResolvedAssetUrls] = useState<Record<string, string>>({});

  // Initialize asset service with auth token
  useEffect(() => {
    assetService.setAccessToken(authToken);
  }, [authToken]);

  // Resolve asset:// URLs for display in the editor
  const resolveAssetUrl = async (assetUrl: string): Promise<string> => {
    if (!assetUrl || !assetUrl.startsWith('asset://')) return assetUrl;
    if (resolvedAssetUrls[assetUrl]) return resolvedAssetUrls[assetUrl];

    try {
      const resolved = await assetService.resolveAssetUrl(assetUrl, tenantId);
      setResolvedAssetUrls(prev => ({ ...prev, [assetUrl]: resolved }));
      return resolved;
    } catch (err) {
      console.warn('Failed to resolve asset URL:', assetUrl, err);
      return '';
    }
  };

  // Resolve all asset URLs when uiElements change
  useEffect(() => {
    const resolveAllAssets = async () => {
      const urls: string[] = [];
      uiElements.forEach(element => {
        if (element.type === 'Control') {
          const items = element.options?.richDropdownItems || [];
          items.forEach(item => {
            if (item.thumbnail?.startsWith('asset://')) {
              urls.push(item.thumbnail);
            }
          });
        } else if (element.type === 'Group') {
          element.elements.forEach(field => {
            const items = field.options?.richDropdownItems || [];
            items.forEach(item => {
              if (item.thumbnail?.startsWith('asset://')) {
                urls.push(item.thumbnail);
              }
            });
          });
        }
      });

      for (const url of urls) {
        if (!resolvedAssetUrls[url]) {
          await resolveAssetUrl(url);
        }
      }
    };

    resolveAllAssets();
  }, [uiElements, tenantId]);

  useEffect(() => {
    fetchWorkflow();
  }, [workflowId]);

  const fetchWorkflow = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/api/workflows/${workflowId}?tenantId=${encodeURIComponent(tenantId)}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch workflow: ${response.statusText}`);
      }

      const data = await response.json();
      setWorkflow(data);

      // Parse UI schema elements
      const pkg = data.package;
      const uiSchema = pkg.schemas.uiSchema;
      const jsonSchema = pkg.schemas.jsonSchema;

      // Convert UI schema elements to editable format with IDs
      const elements = parseUIElements(uiSchema, jsonSchema);
      setUiElements(elements);
      setOriginalElements(JSON.stringify(elements));

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getFieldDefault = (scope: string, jsonSchema: any): any => {
    const fieldName = scope.replace('#/properties/', '');
    return jsonSchema.properties?.[fieldName]?.default;
  };

  const getFieldRequired = (scope: string, jsonSchema: any): boolean => {
    const fieldName = scope.replace('#/properties/', '');
    return Array.isArray(jsonSchema.required) && jsonSchema.required.includes(fieldName);
  };

  const parseUIElements = (uiSchema: any, jsonSchema: any): UIElement[] => {
    // If UI schema has elements array, use it
    if (uiSchema && uiSchema.elements && Array.isArray(uiSchema.elements)) {
      return uiSchema.elements.map((element: any, index: number) => {
        if (element.type === 'Group') {
          return {
            id: `group-${index}`,
            type: 'Group',
            label: element.label || 'Unnamed Group',
            elements: element.elements?.map((child: any, childIndex: number) => ({
            id: `field-${index}-${childIndex}`,
            type: 'Control',
            scope: child.scope,
            label: child.options?.label || getFieldLabel(child.scope, jsonSchema),
            description: getFieldDescription(child.scope, jsonSchema),
            enumValues: getFieldEnum(child.scope, jsonSchema),
            default: getFieldDefault(child.scope, jsonSchema),
            required: getFieldRequired(child.scope, jsonSchema),
            options: child.options || {}
            })) || [],
            options: element.options || {}
          };
        } else if (element.type === 'Control') {
          return {
            id: `field-${index}`,
            type: 'Control',
            scope: element.scope,
            label: element.options?.label || getFieldLabel(element.scope, jsonSchema),
            description: getFieldDescription(element.scope, jsonSchema),
            enumValues: getFieldEnum(element.scope, jsonSchema),
            default: getFieldDefault(element.scope, jsonSchema),
            required: getFieldRequired(element.scope, jsonSchema),
            options: element.options || {}
          };
        }
        return element;
      });
    }

    // Fallback: Generate elements from JSON schema properties
    if (!jsonSchema || !jsonSchema.properties) return [];

    const fieldNames = Object.keys(jsonSchema.properties);
    return fieldNames.map((fieldName, index) => ({
      id: `field-${index}`,
      type: 'Control' as const,
      scope: `#/properties/${fieldName}`,
      label: jsonSchema.properties[fieldName].title || fieldName,
      description: jsonSchema.properties[fieldName].description || '',
      enumValues: jsonSchema.properties[fieldName].enum,
      default: jsonSchema.properties[fieldName].default,
      required: Array.isArray(jsonSchema.required) && jsonSchema.required.includes(fieldName),
      options: {}
    }));
  };

  const getFieldLabel = (scope: string, jsonSchema: any): string => {
    const fieldName = scope.replace('#/properties/', '');
    return jsonSchema.properties?.[fieldName]?.title || fieldName;
  };

  const getFieldDescription = (scope: string, jsonSchema: any): string => {
    const fieldName = scope.replace('#/properties/', '');
    return jsonSchema.properties?.[fieldName]?.description || '';
  };

  const getFieldEnum = (scope: string, jsonSchema: any): string[] | undefined => {
    const fieldName = scope.replace('#/properties/', '');
    return jsonSchema.properties?.[fieldName]?.enum;
  };

  const getFieldName = (scope: string): string => {
    return scope.replace('#/properties/', '');
  };

  const getFieldType = (scope: string): string => {
    if (!workflow) return 'string';
    const fieldName = getFieldName(scope);
    return workflow.package.schemas.jsonSchema.properties?.[fieldName]?.type || 'string';
  };

  const getWidgetType = (element: FieldElement): string => {
    // If widget explicitly set, use it
    if (element.options?.widget) {
      return element.options.widget;
    }
    // Auto-detect: if field has enum values, it's a select widget
    if (element.enumValues && element.enumValues.length > 0) {
      return 'select';
    }
    // Default to text
    return 'text';
  };

  // Check if any changes were made
  const hasChanges = (): boolean => {
    return JSON.stringify(uiElements) !== originalElements;
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const { source, destination } = result;
    const newElements = [...uiElements];

    // Helper function to find and remove an element
    const removeElement = (droppableId: string, index: number): UIElement | null => {
      if (droppableId === 'root') {
        const [removed] = newElements.splice(index, 1);
        return removed;
      } else if (droppableId.startsWith('group-')) {
        const groupIndex = parseInt(droppableId.split('-')[1]);
        const group = newElements[groupIndex] as GroupElement;
        const [removed] = group.elements.splice(index, 1);
        return removed;
      }
      return null;
    };

    // Helper function to insert an element
    const insertElement = (droppableId: string, index: number, element: UIElement) => {
      if (droppableId === 'root') {
        // Only allow groups and controls at root level
        newElements.splice(index, 0, element);
      } else if (droppableId.startsWith('group-')) {
        const groupIndex = parseInt(droppableId.split('-')[1]);
        const group = newElements[groupIndex] as GroupElement;
        // Only allow controls (fields) inside groups
        if (element.type === 'Control') {
          group.elements.splice(index, 0, element as FieldElement);
        } else if (element.type === 'Group') {
          // If dragging a group into another group, extract its fields
          const sourceGroup = element as GroupElement;
          sourceGroup.elements.forEach((field, i) => {
            group.elements.splice(index + i, 0, field);
          });
        }
      }
    };

    // Remove element from source
    const movedElement = removeElement(source.droppableId, source.index);
    if (!movedElement) return;

    // Adjust destination index if moving within the same droppable
    let adjustedDestIndex = destination.index;
    if (source.droppableId === destination.droppableId && source.index < destination.index) {
      adjustedDestIndex--;
    }

    // Insert element at destination
    insertElement(destination.droppableId, adjustedDestIndex, movedElement);

    setUiElements(newElements);
  };

  const handleLabelChange = (elementId: string, newLabel: string, groupIndex?: number) => {
    const newElements = [...uiElements];

    if (groupIndex !== undefined) {
      // Update field label within a group
      const group = newElements[groupIndex] as GroupElement;
      const fieldIndex = group.elements.findIndex(el => el.id === elementId);
      if (fieldIndex !== -1) {
        group.elements[fieldIndex].label = newLabel;
      }
    } else {
      // Update top-level element label
      const elementIndex = newElements.findIndex(el => el.id === elementId);
      if (elementIndex !== -1) {
        if (newElements[elementIndex].type === 'Group') {
          (newElements[elementIndex] as GroupElement).label = newLabel;
        } else {
          (newElements[elementIndex] as FieldElement).label = newLabel;
        }
      }
    }

    setUiElements(newElements);
  };

  const handleDescriptionChange = (elementId: string, newDescription: string, groupIndex?: number) => {
    const newElements = [...uiElements];

    if (groupIndex !== undefined) {
      // Update field description within a group
      const group = newElements[groupIndex] as GroupElement;
      const fieldIndex = group.elements.findIndex(el => el.id === elementId);
      if (fieldIndex !== -1) {
        group.elements[fieldIndex].description = newDescription;
      }
    } else {
      // Update top-level field description
      const elementIndex = newElements.findIndex(el => el.id === elementId);
      if (elementIndex !== -1 && newElements[elementIndex].type === 'Control') {
        (newElements[elementIndex] as FieldElement).description = newDescription;
      }
    }

    setUiElements(newElements);
  };

  const handleHelpTextChange = (elementId: string, newHelpText: string, groupIndex?: number) => {
    const newElements = [...uiElements];

    if (groupIndex !== undefined) {
      // Update field help text within a group
      const group = newElements[groupIndex] as GroupElement;
      const fieldIndex = group.elements.findIndex(el => el.id === elementId);
      if (fieldIndex !== -1) {
        if (!group.elements[fieldIndex].options) {
          group.elements[fieldIndex].options = {};
        }
        group.elements[fieldIndex].options!.helpText = newHelpText;
      }
    } else {
      // Update top-level field help text
      const elementIndex = newElements.findIndex(el => el.id === elementId);
      if (elementIndex !== -1 && newElements[elementIndex].type === 'Control') {
        const field = newElements[elementIndex] as FieldElement;
        if (!field.options) {
          field.options = {};
        }
        field.options.helpText = newHelpText;
      }
    }

    setUiElements(newElements);
  };

  // Update FieldElement properties (default/required) on a top-level or grouped field
  const updateField = (
    elementId: string,
    groupIndex: number | undefined,
    mutator: (field: FieldElement) => void
  ) => {
    const newElements = [...uiElements];
    if (groupIndex !== undefined) {
      const group = newElements[groupIndex] as GroupElement;
      const fieldIndex = group.elements.findIndex(el => el.id === elementId);
      if (fieldIndex !== -1) {
        mutator(group.elements[fieldIndex]);
      }
    } else {
      const elementIndex = newElements.findIndex(el => el.id === elementId);
      if (elementIndex !== -1 && newElements[elementIndex].type === 'Control') {
        mutator(newElements[elementIndex] as FieldElement);
      }
    }
    setUiElements(newElements);
  };

  const handleDefaultChange = (elementId: string, newDefault: any, groupIndex?: number) => {
    updateField(elementId, groupIndex, (field) => {
      // Treat empty-string as "no default" so the property is dropped on save
      if (newDefault === '' || newDefault === undefined || newDefault === null) {
        field.default = undefined;
      } else {
        field.default = newDefault;
      }
    });
  };

  const handleRequiredChange = (elementId: string, isRequired: boolean, groupIndex?: number) => {
    updateField(elementId, groupIndex, (field) => {
      field.required = isRequired;
    });
  };

  const handleHiddenChange = (elementId: string, isHidden: boolean, groupIndex?: number) => {
    updateField(elementId, groupIndex, (field) => {
      if (!field.options) field.options = {};
      if (isHidden) {
        field.options.hidden = true;
      } else {
        delete field.options.hidden;
      }
    });
  };

  const handleCustomEnumChange = (elementId: string, newEnumText: string, groupIndex?: number) => {
    const newElements = [...uiElements];
    
    // Parse enum text (one option per line)
    const enumOptions = newEnumText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    if (groupIndex !== undefined) {
      // Update field custom enum within a group
      const group = newElements[groupIndex] as GroupElement;
      const fieldIndex = group.elements.findIndex(el => el.id === elementId);
      if (fieldIndex !== -1) {
        if (!group.elements[fieldIndex].options) {
          group.elements[fieldIndex].options = {};
        }
        group.elements[fieldIndex].options!.customEnumOptions = enumOptions.length > 0 ? enumOptions : undefined;
      }
    } else {
      // Update top-level field custom enum
      const elementIndex = newElements.findIndex(el => el.id === elementId);
      if (elementIndex !== -1 && newElements[elementIndex].type === 'Control') {
        const field = newElements[elementIndex] as FieldElement;
        if (!field.options) {
          field.options = {};
        }
        field.options.customEnumOptions = enumOptions.length > 0 ? enumOptions : undefined;
      }
    }

    setUiElements(newElements);
  };

  // Rich dropdown item handlers
  const handleRichDropdownItemsChange = (elementId: string, items: RichDropdownItem[], groupIndex?: number) => {
    const newElements = [...uiElements];

    if (groupIndex !== undefined) {
      const group = newElements[groupIndex] as GroupElement;
      const fieldIndex = group.elements.findIndex(el => el.id === elementId);
      if (fieldIndex !== -1) {
        if (!group.elements[fieldIndex].options) {
          group.elements[fieldIndex].options = {};
        }
        group.elements[fieldIndex].options!.richDropdownItems = items.length > 0 ? items : undefined;
      }
    } else {
      const elementIndex = newElements.findIndex(el => el.id === elementId);
      if (elementIndex !== -1 && newElements[elementIndex].type === 'Control') {
        const field = newElements[elementIndex] as FieldElement;
        if (!field.options) {
          field.options = {};
        }
        field.options.richDropdownItems = items.length > 0 ? items : undefined;
      }
    }

    setUiElements(newElements);
  };

  const handleOpenAssetPicker = (fieldId: string, itemIndex: number, groupIndex?: number) => {
    setEditingRichDropdownItem({ fieldId, itemIndex, groupIndex });
    setAssetPickerOpen(true);
  };

  const handleAssetSelected = async (asset: Asset) => {
    if (!editingRichDropdownItem) return;

    const { fieldId, itemIndex, groupIndex } = editingRichDropdownItem;
    const newElements = [...uiElements];

    // Get current items
    let currentItems: RichDropdownItem[] = [];
    if (groupIndex !== undefined) {
      const group = newElements[groupIndex] as GroupElement;
      const field = group.elements.find(el => el.id === fieldId);
      currentItems = [...(field?.options?.richDropdownItems || [])];
    } else {
      const field = newElements.find(el => el.id === fieldId) as FieldElement | undefined;
      currentItems = [...(field?.options?.richDropdownItems || [])];
    }

    // Build the asset URI
    const assetUri = `asset://${asset.category}/${asset.assetId}.${asset.filename.split('.').pop()}`;

    // Update the thumbnail for the specified item
    if (itemIndex < currentItems.length) {
      currentItems[itemIndex] = {
        ...currentItems[itemIndex],
        thumbnail: assetUri
      };
      handleRichDropdownItemsChange(fieldId, currentItems, groupIndex);
    }

    // Resolve the URL immediately so the preview updates right away
    await resolveAssetUrl(assetUri);

    setAssetPickerOpen(false);
    setEditingRichDropdownItem(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Build a Control element's options block, preserving hidden only when true
      const buildControlOptions = (field: FieldElement) => ({
        ...field.options,
        label: field.label,
        helpText: field.options?.helpText || undefined,
        customEnumOptions: field.options?.customEnumOptions || undefined,
        hidden: field.options?.hidden ? true : undefined
      });

      // Convert editable elements back to UI schema format
      const uiSchemaElements = uiElements.map(element => {
        if (element.type === 'Group') {
          const group = element as GroupElement;
          return {
            type: 'Group',
            label: group.label,
            elements: group.elements.map(field => ({
              type: 'Control',
              scope: field.scope,
              options: buildControlOptions(field)
            })),
            options: group.options
          };
        } else {
          const field = element as FieldElement;
          return {
            type: 'Control',
            scope: field.scope,
            options: buildControlOptions(field)
          };
        }
      });

      const updatedUiSchema = {
        type: 'VerticalLayout',
        elements: uiSchemaElements
      };

      // Update JSON Schema with description, default, and required changes
      const updatedJsonSchema = { ...workflow.package.schemas.jsonSchema };
      const getAllFields = (elements: UIElement[]): FieldElement[] => {
        const fields: FieldElement[] = [];
        elements.forEach(element => {
          if (element.type === 'Control') {
            fields.push(element as FieldElement);
          } else if (element.type === 'Group') {
            fields.push(...(element as GroupElement).elements);
          }
        });
        return fields;
      };

      const allFields = getAllFields(uiElements);
      const newRequired: string[] = [];
      allFields.forEach(field => {
        const fieldName = getFieldName(field.scope);
        if (updatedJsonSchema.properties && updatedJsonSchema.properties[fieldName]) {
          updatedJsonSchema.properties[fieldName].description = field.description || '';
          if (field.default === undefined || field.default === '') {
            delete updatedJsonSchema.properties[fieldName].default;
          } else {
            updatedJsonSchema.properties[fieldName].default = field.default;
          }
        }
        if (field.required) {
          newRequired.push(fieldName);
        }
      });
      // Replace required[] with the freshly computed list (drop entirely if empty)
      if (newRequired.length > 0) {
        updatedJsonSchema.required = newRequired;
      } else {
        delete updatedJsonSchema.required;
      }

      const response = await fetch(`${apiBaseUrl}/api/workflows/${workflowId}/presentation?tenantId=${encodeURIComponent(tenantId)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          jsonSchema: updatedJsonSchema,
          uiSchema: updatedUiSchema
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update presentation');
      }

      alert('Workflow presentation updated successfully!');
      onUpdate();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges()) {
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const panelCls  = 'fixed inset-0 z-50 flex';
  const sidePanel = 'w-[560px] flex-shrink-0 bg-white shadow-xl flex flex-col h-full';
  const closeX    = 'w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-fast text-lg';
  const inCls     = 'w-full px-3 py-2 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast placeholder:text-gray-400';
  const taCls     = `${inCls} resize-y`;

  if (loading) return (
    <div className={panelCls}><div className="flex-1 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className={sidePanel}><div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <span className="text-body text-gray-400">Loading presentation…</span>
        <button type="button" onClick={onClose} className={closeX}>×</button>
      </div></div>
    </div>
  );

  if (error && !workflow) return (
    <div className={panelCls}><div className="flex-1 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className={sidePanel}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <span className="text-body text-error">Error: {error}</span>
          <button type="button" onClick={onClose} className={closeX}>×</button>
        </div>
        <div className="px-6 py-4"><button type="button" onClick={onClose}
          className="px-4 py-2 bg-white border border-gray-300 text-label font-medium text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-fast">Close</button></div>
      </div>
    </div>
  );

  if (!workflow) return null;

  const changesDetected = hasChanges();

  // Compact toggle pill matching the existing badge styling
  const togglePillCls = (active: boolean, activeCls: string) =>
    `px-2.5 py-1 rounded-full text-meta font-medium transition-colors duration-fast cursor-pointer select-none ${
      active ? activeCls : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
    }`;

  const renderDefaultValueEditor = (
    element: FieldElement,
    fieldType: string,
    widgetType: string,
    groupIndex?: number
  ) => {
    const enumOptions = element.options?.customEnumOptions || element.enumValues;
    const fieldSchema = workflow?.package?.schemas?.jsonSchema?.properties?.[getFieldName(element.scope)] || {};

    // Enum / select-style fields → constrained dropdown
    if (enumOptions && enumOptions.length > 0) {
      const cur = element.default !== undefined ? String(element.default) : '';
      const valid = cur === '' || enumOptions.includes(cur);
      return (
        <div className="flex flex-col gap-1">
          <select value={cur}
            onChange={e => handleDefaultChange(element.id, e.target.value, groupIndex)}
            aria-label="Default value"
            className={`${inCls} text-label`}>
            <option value="">— No default —</option>
            {enumOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
          {!valid && (
            <p className="text-meta text-warning-dark">⚠ Current default "{cur}" is not in the option list.</p>
          )}
        </div>
      );
    }

    // Boolean fields → tri-state select (none / true / false)
    if (fieldType === 'boolean' || widgetType === 'switch' || widgetType === 'boolean') {
      const cur = element.default === undefined ? '' : element.default ? 'true' : 'false';
      return (
        <select value={cur}
          onChange={e => {
            const v = e.target.value;
            handleDefaultChange(element.id, v === '' ? undefined : v === 'true', groupIndex);
          }}
          aria-label="Default value"
          className={`${inCls} text-label`}>
          <option value="">— No default —</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }

    // Numeric fields → number input with schema-derived bounds and step
    if (fieldType === 'integer' || fieldType === 'number') {
      const step = fieldSchema.multipleOf ?? (fieldType === 'integer' ? 1 : 0.01);
      return (
        <input type="number" value={element.default ?? ''}
          min={fieldSchema.minimum} max={fieldSchema.maximum} step={step}
          onChange={e => {
            const raw = e.target.value;
            if (raw === '') { handleDefaultChange(element.id, undefined, groupIndex); return; }
            const n = fieldType === 'integer' ? parseInt(raw, 10) : parseFloat(raw);
            handleDefaultChange(element.id, Number.isFinite(n) ? n : undefined, groupIndex);
          }}
          placeholder="No default" aria-label="Default value"
          className={`${inCls} text-label`} />
      );
    }

    // String / textarea / image / file → text input
    return (
      <input type="text" value={element.default ?? ''}
        onChange={e => handleDefaultChange(element.id, e.target.value, groupIndex)}
        placeholder="No default" aria-label="Default value"
        className={`${inCls} text-label`} />
    );
  };

  const renderFieldSettings = (
    element: FieldElement,
    fieldType: string,
    widgetType: string,
    groupIndex?: number
  ) => {
    const isHidden = !!element.options?.hidden;
    const isRequired = !!element.required;
    const hasDefault = element.default !== undefined && element.default !== '';
    const hiddenRequiredNoDefault = isHidden && isRequired && !hasDefault;

    return (
      <div className="border border-gray-200 rounded-lg p-3 flex flex-col gap-2 bg-surface-subtle">
        <div className="flex items-center justify-between gap-2">
          <label className="text-meta font-medium text-gray-600 flex items-center gap-1">⚙️ Field Settings</label>
          <div className="flex items-center gap-1.5">
            <button type="button"
              onClick={() => handleRequiredChange(element.id, !isRequired, groupIndex)}
              aria-pressed={isRequired ? 'true' : 'false'}
              title="Toggle whether this field must be filled before submission"
              className={togglePillCls(isRequired, 'bg-error-light text-error')}>
              {isRequired ? '● Required' : '○ Optional'}
            </button>
            <button type="button"
              onClick={() => handleHiddenChange(element.id, !isHidden, groupIndex)}
              aria-pressed={isHidden ? 'true' : 'false'}
              title="Hide this field from the Studio tab; default value is still sent at execution"
              className={togglePillCls(isHidden, 'bg-gray-700 text-white')}>
              {isHidden ? '🙈 Hidden' : '👁 Visible'}
            </button>
          </div>
        </div>
        <div>
          <label className="text-meta font-medium text-gray-500 mb-1 block">Default value</label>
          {renderDefaultValueEditor(element, fieldType, widgetType, groupIndex)}
        </div>
        {hiddenRequiredNoDefault && (
          <p className="text-meta text-error">
            ⚠ This field is hidden and required but has no default — submissions will be blocked.
          </p>
        )}
      </div>
    );
  };

  const renderField = (element: FieldElement, index: number, groupIndex?: number) => {
    const fieldName = getFieldName(element.scope);
    const fieldType = getFieldType(element.scope);
    const widgetType = getWidgetType(element);

    return (
      <Draggable key={element.id} draggableId={element.id} index={index}>
        {(provided, snapshot) => (
          <div ref={provided.innerRef} {...provided.draggableProps}
            className={`flex gap-2 p-3 border border-gray-200 rounded-lg bg-white transition-all duration-fast ${snapshot.isDragging ? 'shadow-lg opacity-90' : 'shadow-xs hover:shadow-sm'}`}>
            {/* Drag handle */}
            <div {...provided.dragHandleProps}
              className="flex-shrink-0 flex items-start pt-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing select-none text-base">
              ⋮⋮
            </div>
            {/* Content */}
            <div className="flex-1 flex flex-col gap-2.5">
              {/* Label + badges */}
              <div className="flex items-center gap-2">
                <input type="text" value={element.label || ''}
                  onChange={e => handleLabelChange(element.id, e.target.value, groupIndex)}
                  placeholder="Field label" aria-label="Field label"
                  className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded-md text-label font-semibold text-gray-900 bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast" />
                <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-meta font-mono flex-shrink-0">{fieldType}</span>
                <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full text-meta font-medium flex-shrink-0">{widgetType}</span>
              </div>
              {/* Description */}
              <input type="text" value={element.description || ''}
                onChange={e => handleDescriptionChange(element.id, e.target.value, groupIndex)}
                placeholder="Field description (optional)" aria-label="Field description"
                className={`${inCls} text-label text-gray-600`} />
              {/* Help text */}
              <div>
                <label className="text-meta font-medium text-gray-500 flex items-center gap-1 mb-1">
                  <span>❓</span> Help Text (shown in Studio)
                </label>
                <textarea value={element.options?.helpText || ''}
                  onChange={e => handleHelpTextChange(element.id, e.target.value, groupIndex)}
                  placeholder="Add helpful guidance for users (optional)" rows={2}
                  className={taCls} />
              </div>
              {/* Field settings: default / required / hidden */}
              {renderFieldSettings(element, fieldType, widgetType, groupIndex)}
              {/* Enum editor */}
              {element.enumValues && element.enumValues.length > 0 && widgetType === 'select' && (
                <div className="border border-gray-200 rounded-lg p-3 flex flex-col gap-2 bg-surface-subtle">
                  <div className="flex items-center justify-between">
                    <label className="text-meta font-medium text-gray-600 flex items-center gap-1">📋 Custom Options</label>
                    <div className="flex items-center gap-2">
                      <span className="text-meta text-gray-400">{element.options?.customEnumOptions?.length || element.enumValues.length} options</span>
                      {element.options?.customEnumOptions && (
                        <button type="button" onClick={() => handleCustomEnumChange(element.id, '', groupIndex)}
                          className="text-meta font-medium text-blue-500 hover:text-blue-600 transition-colors duration-fast">Reset</button>
                      )}
                    </div>
                  </div>
                  <textarea value={(element.options?.customEnumOptions || element.enumValues).join('\n')}
                    onChange={e => handleCustomEnumChange(element.id, e.target.value, groupIndex)}
                    placeholder="Enter custom dropdown options (one per line)" rows={6}
                    className={taCls} />
                  <p className="text-meta text-gray-400">💡 Each line becomes one option.</p>
                </div>
              )}
              {/* Rich dropdown editor */}
              {widgetType === 'rich-dropdown' && (
                <div className="border border-gray-200 rounded-lg p-3 flex flex-col gap-2 bg-surface-subtle">
                  <label className="text-meta font-medium text-gray-600 flex items-center gap-1">🖼️ Rich Dropdown Items</label>
                  <div className="flex flex-col gap-2">
                    {(element.options?.richDropdownItems || []).map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-2">
                        {/* Thumbnail */}
                        <button type="button" onClick={() => handleOpenAssetPicker(element.id, itemIndex, groupIndex)}
                          className="w-12 h-12 flex-shrink-0 rounded-md border-2 border-dashed border-gray-300 hover:border-blue-400 overflow-hidden flex items-center justify-center bg-gray-50 transition-colors duration-fast">
                          {item.thumbnail
                            ? <img src={item.thumbnail.startsWith('asset://')?(resolvedAssetUrls[item.thumbnail]||''):item.thumbnail} alt="" className="w-full h-full object-cover" />
                            : <span className="text-gray-400">📷</span>}
                        </button>
                        {/* Fields */}
                        <div className="flex-1 flex flex-col gap-1">
                          <input type="text" value={item.title} placeholder="Title" aria-label="Item title"
                            onChange={e => { const items=[...(element.options?.richDropdownItems||[])]; items[itemIndex]={...items[itemIndex],title:e.target.value,value:generateSlug(e.target.value)}; handleRichDropdownItemsChange(element.id,items,groupIndex); }}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-md text-label bg-white outline-none focus:border-blue-400 transition-colors duration-fast" />
                          <input type="text" value={item.subtitle||''} placeholder="Subtitle (optional)" aria-label="Item subtitle"
                            onChange={e => { const items=[...(element.options?.richDropdownItems||[])]; items[itemIndex]={...items[itemIndex],subtitle:e.target.value}; handleRichDropdownItemsChange(element.id,items,groupIndex); }}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-md text-label bg-white outline-none focus:border-blue-400 transition-colors duration-fast" />
                        </div>
                        <button type="button" title="Remove item"
                          onClick={() => { const items=[...(element.options?.richDropdownItems||[])]; items.splice(itemIndex,1); handleRichDropdownItemsChange(element.id,items,groupIndex); }}
                          className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full text-gray-400 hover:bg-error-light hover:text-error transition-colors duration-fast text-base">×</button>
                      </div>
                    ))}
                    <button type="button"
                      onClick={() => { const items=[...(element.options?.richDropdownItems||[])]; items.push({value:'',title:'',subtitle:''}); handleRichDropdownItemsChange(element.id,items,groupIndex); }}
                      className="py-2 border-2 border-dashed border-gray-200 rounded-lg text-label font-medium text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors duration-fast">
                      + Add Item
                    </button>
                  </div>
                  <p className="text-meta text-gray-400">💡 Click the thumbnail area to select an image from your Asset Library.</p>
                </div>
              )}
              {/* Field key */}
              <div className="text-meta font-mono text-gray-300">{fieldName}</div>
            </div>
          </div>
        )}
      </Draggable>
    );
  };

  const renderGroup = (element: GroupElement, index: number) => (
    <Draggable key={element.id} draggableId={element.id} index={index}>
      {(provided, snapshot) => (
        <div ref={provided.innerRef} {...provided.draggableProps}
          className={`border-2 border-gray-200 rounded-xl overflow-hidden transition-all duration-fast ${snapshot.isDragging ? 'shadow-lg opacity-90' : 'shadow-xs'}`}>
          {/* Group header */}
          <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div {...provided.dragHandleProps}
              className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing select-none text-base">⋮⋮</div>
            <input type="text" value={element.label} placeholder="Group label" aria-label="Group label"
              onChange={e => handleLabelChange(element.id, e.target.value)}
              className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded-md text-label font-semibold text-gray-900 bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast" />
          </div>
          {/* Group fields drop zone */}
          <Droppable droppableId={element.id}>
            {(prov, snap) => (
              <div ref={prov.innerRef} {...prov.droppableProps}
                className={`p-3 flex flex-col gap-2 min-h-[60px] transition-colors duration-fast ${snap.isDraggingOver ? 'bg-blue-50/40' : 'bg-white'}`}>
                {element.elements.map((field, fi) => renderField(field, fi, index))}
                {prov.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      )}
    </Draggable>
  );

  return (
    <div className={panelCls}>
      <div className="flex-1 bg-black/40" onClick={handleCancel} aria-hidden="true" />
      <div className={sidePanel}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Edit Presentation</h2>
          <button type="button" onClick={handleCancel} className={closeX}>×</button>
        </div>

        {error && <div className="mx-6 mt-3 px-4 py-3 bg-error-light text-error border border-error/30 rounded-md text-label flex-shrink-0">{error}</div>}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {uiElements.length === 0 ? (
            <div className="bg-warning-light border border-warning/30 rounded-lg p-5 text-label text-warning-dark flex flex-col gap-2">
              <p><strong>No workflow parameters found.</strong></p>
              <p>This workflow doesn't have any input parameters defined.</p>
              <p>To add parameters, use the CCStudio custom nodes in ComfyUI:</p>
              <ul className="list-disc list-inside flex flex-col gap-1 ml-2">
                <li><code>CCStudio_WorkflowMeta</code> — defines workflow ID, name, and version</li>
                <li><code>CCStudio_Input</code> — defines user input parameters</li>
                <li><code>CCStudio_Output</code> — defines workflow outputs</li>
              </ul>
              <p>Connect the nodes to your workflow, then re-export and re-import.</p>
            </div>
          ) : (
            <>
              <div className="bg-info-light border border-info/30 rounded-lg px-4 py-3 mb-4 text-label text-info-dark flex flex-col gap-0.5">
                <p><strong>Drag and drop</strong> to reorder fields and groups.</p>
                <p><strong>Drag fields into groups</strong> to organize them into sections.</p>
                <p><strong>Edit labels</strong> to change how fields appear in the Studio.</p>
              </div>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="root">
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}
                      className={`flex flex-col gap-3 min-h-[80px] rounded-xl transition-colors duration-fast p-1 ${snapshot.isDraggingOver ? 'bg-blue-50/30 ring-2 ring-blue-200 ring-inset' : ''}`}>
                      {uiElements.map((element, index) =>
                        element.type === 'Group'
                          ? renderGroup(element as GroupElement, index)
                          : renderField(element as FieldElement, index)
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button type="button" onClick={handleCancel} disabled={saving}
            className="px-4 py-2.5 bg-white border border-gray-300 text-label font-medium text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-fast disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={!changesDetected || saving}
            className="flex-1 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      <AssetPicker isOpen={assetPickerOpen}
        onClose={() => { setAssetPickerOpen(false); setEditingRichDropdownItem(null); }}
        onSelect={handleAssetSelected}
        tenantId={tenantId} authToken={authToken}
        title="Select Thumbnail" categoryFilter="thumbnails" />
    </div>
  );
};


